/**
 * Marketplace header search autocomplete (Blade).
 *
 * Mirrors Vue `Autocomplete.vue` (identifiant="autocomplete") wired to InstantSearch
 * on `#header-searchbox-mobile` — recent searches, iconic brands, query suggestions.
 */

import { autocomplete } from "@algolia/autocomplete-js";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";
import { connectSearchBox } from "instantsearch.js/es/connectors";
import type { InstantSearch } from "instantsearch.js";

import porscheSvg from "../../images/porsche.svg";
import ferrariSvg from "../../images/ferrari.svg";
import audiSvg from "../../images/audi.svg";
import mercedesSvg from "../../images/mercedes.svg";
import mclarenSvg from "../../images/mclaren.svg";
import lamborghiniSvg from "../../images/lamborghini.svg";

import {
    SearchClientFactory,
    type SearchConfig,
} from "../services/SearchClientFactory";
import { redirectToMarketplace } from "../shared/autocomplete-factory";
import {
    clearPillSearchPanelOpenState,
    pillSearchPanelLiantOptions,
} from "../shared/pill-search-panel-liant";
import { installAutocompleteScrollGuard } from "../shared/autocomplete-scroll-guard";

/** Must match Vue MarketplaceHeader `identifiant="autocomplete"`. */
const AUTOCOMPLETE_IDENT = "autocomplete";
const RECENT_SEARCHES_STORAGE_KEY = `instantsearch-${AUTOCOMPLETE_IDENT}`;

const brandData = [
    { label: "Porsche", img: porscheSvg },
    { label: "Ferrari", img: ferrariSvg },
    { label: "Audi", img: audiSvg },
    { label: "Mercedes", img: mercedesSvg },
    { label: "McLaren", img: mclarenSvg },
    { label: "Lamborghini", img: lamborghiniSvg },
];

function getRecentSearchesCount(recentSearchesPlugin: {
    data?: { getAll: () => unknown };
}): number {
    if (recentSearchesPlugin.data) {
        const recentSearches = recentSearchesPlugin.data.getAll();
        return Array.isArray(recentSearches) ? recentSearches.length : 0;
    }
    return 0;
}

function hasWordStartingWith(text: string, query: string): boolean {
    if (!text || !query) return false;
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase().trim();
    if (!queryLower.includes(" ")) {
        return textLower
            .split(/\s+/)
            .some((word) => word.startsWith(queryLower));
    }
    const queryWords = queryLower.split(/\s+/);
    const textWords = textLower.split(/\s+/);
    for (let i = 0; i <= textWords.length - queryWords.length; i++) {
        let allMatch = true;
        for (let j = 0; j < queryWords.length; j++) {
            const textWord = textWords[i + j];
            const queryWord = queryWords[j];
            if (j === 0) {
                if (
                    !textWord.includes(queryWord) &&
                    !textWord.startsWith(queryWord)
                ) {
                    allMatch = false;
                    break;
                }
            } else if (!textWord.startsWith(queryWord)) {
                allMatch = false;
                break;
            }
        }
        if (allMatch) return true;
    }
    return false;
}

function findWordMatch(
    text: string,
    query: string,
): { start: number; end: number } | null {
    if (!text || !query) return null;
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase().trim();
    if (!queryLower.includes(" ")) {
        const words = textLower.split(/\s+/);
        let currentIndex = 0;
        for (const word of words) {
            const wordStartIndex = textLower.indexOf(word, currentIndex);
            if (word.startsWith(queryLower)) {
                return {
                    start: wordStartIndex,
                    end: wordStartIndex + queryLower.length,
                };
            }
            currentIndex = wordStartIndex + word.length;
        }
        return null;
    }
    const queryWords = queryLower.split(/\s+/);
    const firstQueryWord = queryWords[0];
    const firstWordIndex = textLower.indexOf(firstQueryWord);
    if (firstWordIndex === -1) return null;
    return {
        start: firstWordIndex,
        end: Math.min(firstWordIndex + queryLower.length, textLower.length),
    };
}

function getShortestMatchingWordLength(text: string, query: string): number {
    if (!text || !query) return Infinity;
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase().trim();
    if (!queryLower.includes(" ")) {
        let shortestLength = Infinity;
        for (const word of textLower.split(/\s+/)) {
            if (word.startsWith(queryLower)) {
                shortestLength = Math.min(shortestLength, word.length);
            }
        }
        return shortestLength;
    }
    return hasWordStartingWith(textLower, queryLower) ? queryLower.length : Infinity;
}

type MarketplaceAutocompleteCallbacks = {
    onQueryChange: (query: string) => void;
    onQuerySelect: (query: string) => void;
    onMakeSelect: (make: string) => void;
};

/**
 * InstantSearch searchBox refine is gated: only armed commits (submit / select / reset)
 * reach Typesense. Keyboard preview via autocomplete `completion` must never trigger
 * a search — do not sync query on autocomplete `onStateChange`.
 */
export function createCommitGatedQueryHook() {
    let allowNextRefine = false;

    return {
        armRefine: () => {
            allowNextRefine = true;
        },
        queryHook: (query: string, search: (value: string) => void) => {
            if (!allowNextRefine) {
                return;
            }
            allowNextRefine = false;
            search(query);
        },
    };
}

function createQuerySuggestionsPlugin(
    autocompleteSearchClient: any,
    indexName: string,
    callbacks: MarketplaceAutocompleteCallbacks,
) {
    return {
        getSources({ query }: { query: string }) {
            if (!query) return [];

            return [
                {
                    sourceId: "querySuggestionsPlugin",
                    async getItems() {
                        try {
                            const results = await autocompleteSearchClient.search([
                                {
                                    indexName,
                                    params: {
                                        query,
                                        hitsPerPage: 50,
                                        attributesToRetrieve: [
                                            "suggestion_make",
                                            "suggestion_make_model",
                                            "suggestion_make_model_specification",
                                        ],
                                    },
                                },
                            ]);

                            const suggestions: Array<{ query: string }> = [];
                            const seenQueries = new Set<string>();
                            const queryLower = query.toLowerCase();

                            const makeSuggestions: Array<{ query: string }> = [];
                            const makeModelSuggestions: Array<{ query: string }> =
                                [];
                            const makeModelSpecificationSuggestions: Array<{
                                query: string;
                            }> = [];

                            results.results[0].hits.forEach((hit: any) => {
                                if (
                                    hit.suggestion_make &&
                                    !seenQueries.has(hit.suggestion_make) &&
                                    hasWordStartingWith(
                                        hit.suggestion_make,
                                        queryLower,
                                    )
                                ) {
                                    makeSuggestions.push({
                                        query: hit.suggestion_make,
                                    });
                                    seenQueries.add(hit.suggestion_make);
                                }
                                if (
                                    hit.suggestion_make_model &&
                                    !seenQueries.has(hit.suggestion_make_model) &&
                                    hasWordStartingWith(
                                        hit.suggestion_make_model,
                                        queryLower,
                                    )
                                ) {
                                    makeModelSuggestions.push({
                                        query: hit.suggestion_make_model,
                                    });
                                    seenQueries.add(hit.suggestion_make_model);
                                }
                                if (
                                    hit.suggestion_make_model_specification &&
                                    !seenQueries.has(
                                        hit.suggestion_make_model_specification,
                                    ) &&
                                    hasWordStartingWith(
                                        hit.suggestion_make_model_specification,
                                        queryLower,
                                    )
                                ) {
                                    makeModelSpecificationSuggestions.push({
                                        query: hit.suggestion_make_model_specification,
                                    });
                                    seenQueries.add(
                                        hit.suggestion_make_model_specification,
                                    );
                                }
                            });

                            const sortByMatchLength = (
                                items: Array<{ query: string }>,
                            ) =>
                                items.sort((a, b) => {
                                    const lengthA = getShortestMatchingWordLength(
                                        a.query,
                                        queryLower,
                                    );
                                    const lengthB = getShortestMatchingWordLength(
                                        b.query,
                                        queryLower,
                                    );
                                    if (lengthA !== lengthB) return lengthA - lengthB;
                                    return a.query.localeCompare(b.query);
                                });

                            suggestions.push(...sortByMatchLength(makeSuggestions));
                            suggestions.push(
                                ...sortByMatchLength(makeModelSuggestions),
                            );
                            suggestions.push(
                                ...sortByMatchLength(
                                    makeModelSpecificationSuggestions,
                                ),
                            );
                            return suggestions.slice(0, 7);
                        } catch {
                            return [];
                        }
                    },
                    getItemInputValue({ item }: { item: { query: string } }) {
                        return item.query;
                    },
                    onSelect({ item }: { item: { query: string } }) {
                        callbacks.onQuerySelect(item.query);
                    },
                    templates: {
                        item({
                            item,
                            html,
                            state,
                        }: {
                            item: { query: string };
                            html: any;
                            state: { query?: string };
                        }) {
                            const formattedQuery = item.query;
                            const userInput = (state.query || "").toLowerCase();
                            if (!userInput) {
                                return html`<span class="aa-ItemContentTitle">${formattedQuery}</span>`;
                            }
                            const wordMatch = findWordMatch(
                                formattedQuery,
                                userInput,
                            );
                            if (!wordMatch) {
                                return html`<span class="aa-ItemContentTitle">${formattedQuery}</span>`;
                            }
                            const beforeMatch = formattedQuery.substring(
                                0,
                                wordMatch.start,
                            );
                            const match = formattedQuery.substring(
                                wordMatch.start,
                                wordMatch.end,
                            );
                            const afterMatch = formattedQuery.substring(
                                wordMatch.end,
                            );
                            return html`<span class="aa-ItemContentTitle">
                                ${beforeMatch}<mark>${match}</mark>${afterMatch}
                            </span>`;
                        },
                    },
                },
            ];
        },
    };
}

function createRecentSearchesPlugin(callbacks: MarketplaceAutocompleteCallbacks) {
    return createLocalStorageRecentSearchesPlugin({
        key: RECENT_SEARCHES_STORAGE_KEY,
        limit: 3,
        transformSource({ source }) {
            return {
                ...source,
                getItems(params: any) {
                    if (params.query && params.query.length > 0) return [];
                    return source.getItems(params);
                },
                onSelect({ item }: { item: { label: string } }) {
                    callbacks.onQuerySelect(item.label);
                },
                templates: {
                    ...source.templates,
                    item(params: { item: { label: string }; html: any }) {
                        const { item, html } = params;
                        return html`<span class="aa-ItemLink">${item.label}</span>`;
                    },
                },
            };
        },
    });
}

function initMarketplaceHeaderAutocomplete(
    containerSelector: string,
    placeholder: string,
    searchConfig: SearchConfig,
    initialQuery: string,
    callbacks: MarketplaceAutocompleteCallbacks,
    redirectToMarketplaceOnSelect: boolean,
): ReturnType<typeof autocomplete> | null {
    const container = document.querySelector(containerSelector);
    if (!container) return null;

    installAutocompleteScrollGuard();

    container.innerHTML = "";

    const recentSearchesPlugin = createRecentSearchesPlugin(callbacks);
    const plugins: any[] = [recentSearchesPlugin];

    if (searchConfig.driver === "typesense") {
        try {
            const autocompleteSearchClient =
                SearchClientFactory.createAutocompleteClient(searchConfig);
            plugins.push(
                createQuerySuggestionsPlugin(
                    autocompleteSearchClient,
                    searchConfig.indexName,
                    callbacks,
                ),
            );
        } catch {
            // Recent searches still work without suggestions
        }
    }

    return autocomplete({
        container: containerSelector,
        placeholder,
        detachedMediaQuery: "none",
        openOnFocus: true,
        ...pillSearchPanelLiantOptions(),
        initialState: { query: initialQuery },
        plugins,
        onSubmit({ state }) {
            if (redirectToMarketplaceOnSelect) {
                redirectToMarketplace({ query: state.query });
                return;
            }
            callbacks.onQueryChange(state.query);
        },
        onReset() {
            callbacks.onQueryChange("");
        },
        getSources(): any {
            return [
                {
                    sourceId: "brands",
                    getItems({ query }: any) {
                        if (query && query.length > 0) return [];
                        return brandData.filter(({ label }) =>
                            label
                                .toLowerCase()
                                .includes((query || "").toLowerCase()),
                        );
                    },
                    onSelect({ item }: any) {
                        callbacks.onMakeSelect(item.label);
                    },
                    templates: {
                        item({ item, html }: any) {
                            return html`<span class="aa-ItemLink">
                                <img
                                    class="aa-Item--icon"
                                    src="${item.img}"
                                    alt="${item.label}"
                                />
                                ${item.label}
                            </span>`;
                        },
                    },
                },
            ];
        },
        render({ elements, render, html, state }, root) {
            const {
                recentSearchesPlugin: recentEl,
                querySuggestionsPlugin: suggestionsEl,
                brands,
            } = elements;
            const recentSearchesCount =
                getRecentSearchesCount(recentSearchesPlugin);

            if (!state.query) {
                render(
                    html`<div class="aa-PanelLayout aa-Panel--scrollable">
                        <div class="aa-PanelSections">
                            ${recentSearchesCount > 0
                                ? html`<div>
                                      <div class="aa-SourceHeader">
                                          <span class="aa-SourceHeaderTitle">
                                              Recherches récentes
                                          </span>
                                      </div>
                                      ${recentEl}
                                  </div>`
                                : ""}
                            <div>
                                <div class="aa-SourceHeader">
                                    <span class="aa-SourceHeaderTitle"
                                        >Marques emblématiques</span
                                    >
                                </div>
                                <div class="aa-PanelSectionBrands">
                                    ${brands}
                                </div>
                            </div>
                        </div>
                    </div>` as any,
                    root,
                );
            } else {
                render(
                    html`<div class="aa-PanelLayout aa-Panel--scrollable">
                        <div class="aa-PanelSections">
                            <div>${suggestionsEl || ""}</div>
                        </div>
                    </div>` as any,
                    root,
                );
            }
        },
    });
}

export function initStandalonePillHeaderSearch(options?: {
    container?: string;
    placeholder?: string;
}): ReturnType<typeof autocomplete> | null {
    const searchConfig = (window as unknown as { __SEARCH_CONFIG__?: SearchConfig })
        .__SEARCH_CONFIG__;
    if (!searchConfig) {
        return null;
    }

    const callbacks: MarketplaceAutocompleteCallbacks = {
        onQueryChange: () => {},
        onQuerySelect: (query) => redirectToMarketplace({ query }),
        onMakeSelect: (make) => redirectToMarketplace({ make }),
    };

    return initMarketplaceHeaderAutocomplete(
        options?.container ?? "#header-searchbox-mobile",
        options?.placeholder ?? "Marque, modèle, finition…",
        searchConfig,
        "",
        callbacks,
        true,
    );
}

export function createMarketplaceHeaderAutocompleteWidget(
    instantSearch: InstantSearch,
    searchConfig: SearchConfig,
    options: {
        container: string;
        placeholder: string;
        redirectToMarketplaceOnSelect?: boolean;
    },
) {
    const redirectToMarketplaceOnSelect = Boolean(
        options.redirectToMarketplaceOnSelect,
    );
    let autocompleteInstance: ReturnType<typeof autocomplete> | null = null;
    let syncedQuery = "";
    const { armRefine, queryHook } = createCommitGatedQueryHook();

    const indexName = searchConfig.indexName;

    const setMakeFilter = (make: string) => {
        instantSearch.setUiState((uiState) => ({
            ...uiState,
            [indexName]: {
                ...uiState[indexName],
                page: 1,
                menu: {
                    ...(uiState[indexName]?.menu ?? {}),
                    make,
                },
            },
        }));
    };

    return connectSearchBox(({ query, refine }, isFirstRender) => {
        const commitQuery = (nextQuery: string) => {
            syncedQuery = nextQuery;
            armRefine();
            refine(nextQuery);
        };

        const callbacks: MarketplaceAutocompleteCallbacks = {
            onQueryChange: commitQuery,
            onQuerySelect: (nextQuery) => {
                if (redirectToMarketplaceOnSelect) {
                    redirectToMarketplace({ query: nextQuery });
                    return;
                }
                autocompleteInstance?.setQuery(nextQuery);
                commitQuery(nextQuery);
            },
            onMakeSelect: (make) => {
                if (redirectToMarketplaceOnSelect) {
                    redirectToMarketplace({ make });
                    return;
                }
                setMakeFilter(make);
            },
        };

        if (isFirstRender) {
            autocompleteInstance = initMarketplaceHeaderAutocomplete(
                options.container,
                options.placeholder,
                searchConfig,
                query,
                callbacks,
                redirectToMarketplaceOnSelect,
            );
            syncedQuery = query;
            return;
        }

        if (autocompleteInstance && query !== syncedQuery) {
            syncedQuery = query;
            autocompleteInstance.setQuery(query);
        }
    })({ queryHook });
}
